/**
 * GFG-405 — the gift-link quantity is always a whole number within LINK_QTY_MIN..LINK_QTY_MAX
 * (1..10,000, the API's bounds), wherever it is typed or stepped (the campaign builder and the Add
 * gift links wizard share these rules).
 */

import { LINK_QTY_MAX, LINK_QTY_MIN } from "@giftforward/naming";
import type { NotificationMode } from "@/interfaces/CampaignBuilder";

/**
 * A whole number inside the range. NaN reads as the minimum; +Infinity clamps to the maximum and
 * -Infinity to the minimum (the builder's original `Math.min(MAX, Math.max(MIN, n))` behaviour).
 */
export function clampLinkQty(n: number): number {
  if (Number.isNaN(n)) return LINK_QTY_MIN;
  return Math.min(LINK_QTY_MAX, Math.max(LINK_QTY_MIN, Math.trunc(n)));
}

/** The quantity committed from the typed text (on blur): unreadable text is the minimum. */
export function commitLinkQtyText(text: string): number {
  return clampLinkQty(Number.parseInt(text, 10));
}

/** One step up or down from the typed text; an empty or unreadable box counts as 0. */
export function stepLinkQty(text: string, delta: 1 | -1): number {
  return clampLinkQty((Number.parseInt(text, 10) || 0) + delta);
}

/** The clamped quantity, and whether the plan cap (not the hard bounds) is what lowered it. */
interface LinkCapClamp {
  value: number;
  overCap: boolean;
}

/**
 * GFG-439: the stepper's quantity under the plan's recipient cap. `null` is an unlimited plan and
 * never lowers the value. A cap above the hard maximum never lifts it.
 */
export function clampToLinkCap(n: number, cap: number | null): LinkCapClamp {
  const base = clampLinkQty(n);
  if (cap === null) return { value: base, overCap: false };
  const effectiveCap = Math.min(cap, LINK_QTY_MAX);
  return { value: Math.min(base, effectiveCap), overCap: base > effectiveCap };
}

/** Step 2 predicate: does the draft's quantity exceed the cap for a mode that spends one link per recipient? */
export function linkQuantityOverCap(
  state: { notificationMode: NotificationMode; linkToList: boolean | null; linkQuantity: number },
  cap: number | null,
): boolean {
  if (cap === null) return false;
  const mintsLinks =
    state.notificationMode === "universal" ||
    (state.notificationMode === "manual" && state.linkToList === false);
  return mintsLinks && state.linkQuantity > cap;
}

/** Step 2 hold: the over-cap predicate, waived once the campaign's recipients are locked. */
export function stepTwoCapHold(
  state: {
    notificationMode: NotificationMode;
    linkToList: boolean | null;
    linkQuantity: number;
    recipientsLocked: boolean;
  },
  cap: number | null,
): boolean {
  return !state.recipientsLocked && linkQuantityOverCap(state, cap);
}

/** True when a forward jump passes through Step 2 (so its cap hold must run). */
export function crossesStepTwoForward(from: number, to: number): boolean {
  return from <= 2 && to > 2;
}
